import { useEffect, useRef, useState } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { Star, Quote } from 'lucide-react';

export default function Testimonials() {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.3 });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        duration: 0.8
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const testimonialVariants = {
    enter: { opacity: 0, x: 50 },
    center: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -50 }
  };

  const testimonials = [
    {
      name: "Talita Wuilleme",
      company: "CPHR Student Member",
      content: " I strongly recommend Jasprit (Jazz), a colleague and manager in the past for employment. I have had the opportunity to observe Jaspreet's professional skills as well as interpersonal style and learn a lot from him. He is consistently pleasant, and takes on all assignments with enthusiasm and dedication. He set a great example for everyone who worked under his supervision at Danier Leather",
      rating: 5
    },
    {
      name: "Moses Baydir",
      company: "General Manager at Corwin Metal Products Inc.",
      content: "Jasprit (Jazz) is a true professional with great knowledge! I have had the privilege working with Jazz, he was thorough, honest and helpful. I am very happy that I have earned an honest friend like him. Would definitely recommend Jasprit (Jazz)!.",
      rating: 5
    },
    {
      name: "Vinit Amin",
      company: "Portfolio Manager II - Entertainment Banking",
      content: "I had the opportunity to work with Jasprit (Jazz) little over a year at RBC Royal Bank of Canada. His interpersonal skills and ability to understand client's needs & deliver an extraordinary service is exceptional. Jasprit (Jazz) would be a true asset to any team.",
      rating: 5
    },
    {
      name: "Amritpal Singh",
      company: "Sr.Manager Basel Operations @ Equitable Bank",
      content: "Jasprit (Jazz) and me went to the same University. He is highly disciplined, reliable, knows his stuff really well, very confident and willing to give his opinions. Highly committed and responsible to deliver his tasks. He manages and priorities the work really well.",
      rating: 5
    }
  ];

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <motion.div
        key={i}
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: i * 0.1 }}
      >
        <Star
          className={`w-5 h-5 ${i < rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
        />
      </motion.div>
    ));
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section ref={sectionRef} className="py-24 bg-gradient-to-b from-white to-slate-50 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div 
          className="text-center mb-16"
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          <motion.h2 variants={itemVariants} className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            Client <span className="text-emerald-500">Success Stories</span>
          </motion.h2>
          <motion.p variants={itemVariants} className="text-xl text-slate-600 max-w-3xl mx-auto">
            Hear from businesses and investors who have achieved their financing goals with Done Commercials
          </motion.p>
        </motion.div>

        <motion.div 
          className="max-w-4xl mx-auto"
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 relative">
            <Quote className="w-12 h-12 text-emerald-100 absolute top-6 left-6" />
            
            <div className="relative z-10">
              <div className="flex justify-center mb-6">
                <div className="flex space-x-1">
                  {renderStars(testimonials[currentTestimonial].rating)}
                </div>
              </div>
              
              <AnimatePresence mode="wait">
                <motion.blockquote 
                  key={currentTestimonial}
                  variants={testimonialVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: 0.5 }}
                  className="text-xl md:text-2xl text-slate-700 text-center mb-8 leading-relaxed"
                >
                  "{testimonials[currentTestimonial].content}"
                </motion.blockquote>
              </AnimatePresence>
              
              <AnimatePresence mode="wait">
                <motion.div 
                  key={currentTestimonial}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.5 }}
                  className="text-center"
                >
                  <p className="text-lg font-semibold text-slate-900">
                    {testimonials[currentTestimonial].name}
                  </p>
                  <p className="text-slate-600">
                    {testimonials[currentTestimonial].company}
                  </p>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          <div className="flex justify-center mt-8 space-x-3">
            {testimonials.map((_, index) => (
              <motion.button
                key={index}
                onClick={() => setCurrentTestimonial(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  index === currentTestimonial ? 'bg-emerald-500' : 'bg-slate-300'
                }`}
                whileHover={{ scale: 1.5 }}
                whileTap={{ scale: 0.8 }}
              />
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}